import React, { useState } from 'react';
import PageHeading from '../components/common/PageHeading';
import SectionTitle from '../components/common/SectionTitle';
import Timeline from '../components/common/Timeline';
import Input from '../components/common/Input';
import TextArea from '../components/common/TextArea';
import { Button, Checkbox } from '@heroui/react';
import 'swiper/css';
import { Notify } from 'notiflix';
import { Helmet } from 'react-helmet';

function Admission() {
    const DOMAIN = "https://oxfordcollege.lk/api/routes/";

    const [formData, setFormData] = useState({
        studentFirstName: '',
        studentLastName: '',
        email: '',
        phone: '',
        message: '',
        date: new Date().toLocaleDateString(),
    });

    const [acceptTerms, setAcceptTerms] = useState(false); // Separate state for acceptTerms
    const [errors, setErrors] = useState({});

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: '',
        });
    };

    const handleCheckboxChange = (e) => {
        const { checked } = e.target;
        setAcceptTerms(checked); // Update the acceptTerms state
        setErrors({
            ...errors,
            acceptTerms: '',
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.studentFirstName.trim()) {
            newErrors.studentFirstName = 'First Name is required';
        }

        if (!formData.studentLastName.trim()) {
            newErrors.studentLastName = 'Last Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(formData.phone)) {
            newErrors.phone = 'Phone number must be 10 digits';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }

        if (!acceptTerms) {
            newErrors.acceptTerms = 'You must accept the terms and conditions';
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formErrors = validateForm();

        if (Object.keys(formErrors).length === 0) {

            // Send form data using fetch POST
            fetch(DOMAIN + 'inquiry.php', {
                method: 'POST',
                body: JSON.stringify(formData),
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        // Show success message
                        Notify.success('Form submitted successfully');
                        setFormData({
                            studentFirstName: '',
                            studentLastName: '',
                            email: '',
                            phone: '',
                            message: '',
                            date: new Date().toLocaleDateString(),
                        });
                        setAcceptTerms(false); // Reset acceptTerms
                        return response.json();
                    } else if (response.status === 400) {
                        // Show error message
                        Notify.failure('Form submission failed. Please check the form for errors.');
                        throw new Error('Bad Request');
                    } else if (response.status === 500) {
                        // Show error message
                        Notify.failure('Form submission failed. Please try again later.');
                        throw new Error('Server Error');
                    } else {
                        // Show error message
                        Notify.failure('Form submission failed. Please try again later.');
                        throw new Error('Unknown Error');
                    }
                })
                .catch((error) => console.error('Error:', error));
        } else {
            Notify.failure('Form submission failed. Please check the form for errors.');
        }
    };

    return (
        <>
            <Helmet>
                <title>Admission - Oxford College</title>
                <meta name="description" content="Explore the admission process for Oxford College and take the first step toward your future." />
                <meta name="keywords" content="Admission, Apply, Enrollment, Oxford College, Future" />
                <meta name="author" content="Oxford College - Gampaha" />
                <meta property="og:title" content="Admission - Oxford College - Gampaha" />
                <meta property="og:description" content="Learn about the admission process and apply to join Oxford College today." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://oxfordcollege.lk/admission" />
                <meta property="og:image" content="https://www.facebook.com/photo/?fbid=127196623392555&set=a.127196626725888" />
            </Helmet>

            <div className="flex flex-col min-h-screen ">
                <PageHeading
                    imgUrl='./assets/images/admissions-sign.webp'
                    alt='programs and courses'
                    title='Admissions'
                />
                <div className='container mx-auto'>
                    <section className='mt-8 md:mt-12 lg:mt-16 px-4 md:px-6 lg:container mx-auto'>
                        <SectionTitle
                            titleText={
                                <>
                                    We welcome your child to learn and thrive, <br /> dreams come alive... At Oxford
                                    College.
                                </>
                            }
                            textAlignment='center'
                        />
                        <p className='text-center py-8'>At Oxford International Gampaha <br /> We offer a holistic
                            education experience that goes beyond academics to include character development, global
                            awareness, and lifelong learning skills.
                            <br /> Our students thrive in a supportive environment that nurtures their individual talents
                            and prepares them for the challenges of tomorrow.</p>
                    </section>
                    <section className='lg:px-4 px-2'>
                        <div className='max-h-[500px] bg-gray-200 w-full h-full relative overflow-hidden rounded-3xl'>
                            <img src="./assets/images/admission-banner.webp" alt="oxford international - Gampaha" />
                            <div
                                className="h-full w-full bg-gradient-to-r from-primary-blue/40 via-primary-blue/10 to-primary-blue/0 absolute top-0 left-0"></div>
                            <div
                                className="bg-[#1C1D71] p-4 absolute top-1/2 lg:top-3/4 transform left-2 md:left-8 -translate-y-1/2 rounded-lg">
                                <img src="./assets/icons/cambridge-logo.svg" className='h-24'
                                    alt="cambridge-english-logo" />
                            </div>
                        </div>
                    </section>

                    <section
                        className='grid grid-cols-1 justify-center md:grid-cols-4 lg:grid-cols-5 gap-4 lg:px-4 px-2 py-8'>
                        <div className="col-span-full md:col-span-3 lg:col-span-3 xl:pe-20">
                            <Timeline />
                        </div>
                        <div className="col-span-full lg:col-span-2">
                            <div className='max-w-[400px] mx-auto'>
                                <div className="bg-neutral-100 p-4 rounded-lg w-full">
                                    <h3 className="text-4xl text-primary-blue font-bebas">Inquiry Form</h3>
                                    <form onSubmit={handleSubmit}>
                                        <Input
                                            label={'Student First Name'}
                                            name={'studentFirstName'}
                                            type={'text'}
                                            value={formData.studentFirstName}
                                            onChange={handleFormChange}
                                            error={errors.studentFirstName}
                                        />
                                        <Input
                                            label={'Student Last Name'}
                                            name={'studentLastName'}
                                            type={'text'}
                                            value={formData.studentLastName}
                                            onChange={handleFormChange}
                                            error={errors.studentLastName}
                                        />

                                        <Input
                                            label={'Email Address'}
                                            name={'email'}
                                            type={'email'}
                                            value={formData.email}
                                            onChange={handleFormChange}
                                            error={errors.email}
                                        />

                                        <Input
                                            label={'Phone Number'}
                                            name={'phone'}
                                            type={'tel'}
                                            value={formData.phone}
                                            onChange={handleFormChange}
                                            error={errors.phone}
                                        />

                                        <TextArea
                                            label={'Message'}
                                            name={'message'}
                                            value={formData.message}
                                            onChange={handleFormChange}
                                            error={errors.message}
                                        />

                                        <Checkbox
                                            name="acceptTerms"
                                            isSelected={acceptTerms} // Use the separate state
                                            onChange={handleCheckboxChange}
                                            color='default'
                                            size="sm"
                                            className='text-tiny'
                                        >
                                            Accept Terms and Conditions
                                        </Checkbox>
                                        {errors.acceptTerms && (
                                            <p className="text-red-500 text-sm mt-1">{errors.acceptTerms}</p>
                                        )}

                                        <Button
                                            radius={'sm'}
                                            size={'lg'}
                                            type="submit"
                                            className='text-lg w-full mt-4 bg-primary-blue text-white'
                                            variant='contained'
                                        >
                                            Submit
                                        </Button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div
                            className='bg-primary-blue w-full h-auto p-10 text-white relative overflow-hidden rounded-3xl'>
                            <p className='max-w-[800px] mx-auto text-center'>
                                Join us today and take the first step towards achieving your academic and personal
                                goals! Enroll now to secure your spot in our exceptional programs and start your journey
                                towards success. Don’t miss out – your future begins here!
                            </p>
                            <Button radius={'sm'} size={'lg'}
                                onPress={() => window.location.href = '/admission-form'}
                                className='text-lg mt-10 bg-secondary-blue text-black font-semibold w-fit mx-auto block'
                                variant='contained'>
                                Click Here to Enroll
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
}

export default Admission;